/* ====================================================================
   FATHER'S DAY COLLECTION — MOTION & INTERACTION ENGINE
   Loaded once (guarded) by every fd-* section.
   Everything is idempotent and safe to re-run after a Theme Editor
   section re-render (shopify:section:load).
   ==================================================================== */
(function () {
  'use strict';

  if (window.FDCore && window.FDCore.__v === 1) {
    // Script tag duplicated across sections — just re-scan the DOM.
    window.FDCore.init();
    return;
  }

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var HOVER = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }
  // Marks an element as initialised. The key is namespaced so the flag can
  // never collide with a functional attribute — `once(root, 'filter')` must
  // not end up writing data-fd-filter, which is what the pills are read from.
  function once(el, key) {
    var flag = 'fdInit' + key.charAt(0).toUpperCase() + key.slice(1);
    if (el.dataset[flag] === '1') return false;
    el.dataset[flag] = '1';
    return true;
  }

  /* ==================================================================
     1. SCROLL REVEAL
     ================================================================== */
  var revealIO = null;

  function initReveal() {
    var nodes = all('[data-fd-reveal]').filter(function (el) {
      return once(el, 'reveal');
    });
    if (!nodes.length) return;

    if (REDUCED) {
      nodes.forEach(function (el) {
        el.classList.add('fd-revealed');
      });
      return;
    }

    if (!revealIO) {
      revealIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e) {
            if (!e.isIntersecting) return;
            e.target.classList.add('fd-revealed');
            revealIO.unobserve(e.target);
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
      );
    }

    nodes.forEach(function (el) {
      // Auto-stagger children of a [data-fd-stagger] parent.
      var parent = el.closest('[data-fd-stagger]');
      if (parent && !el.style.getPropertyValue('--fd-delay')) {
        var step = parseInt(parent.dataset.fdStagger, 10) || 70;
        var kids = all('[data-fd-reveal]', parent);
        var i = kids.indexOf(el);
        if (i > -1) el.style.setProperty('--fd-delay', Math.min(i * step, 640) + 'ms');
      }
      revealIO.observe(el);
    });
  }

  /* ==================================================================
     2. SPLIT-WORD HEADLINE REVEAL
     ================================================================== */
  function splitNode(node, state) {
    // Walk text nodes so inline markup (<em>, <span class="fd-accent">) survives.
    Array.prototype.slice.call(node.childNodes).forEach(function (child) {
      if (child.nodeType === 3) {
        var text = child.textContent;
        if (!text.trim()) return;
        var frag = document.createDocumentFragment();
        text.split(/(\s+)/).forEach(function (chunk) {
          if (!chunk) return;
          if (!chunk.trim()) {
            frag.appendChild(document.createTextNode(chunk));
            return;
          }
          var w = document.createElement('span');
          w.className = 'fd-word';
          var inner = document.createElement('i');
          inner.textContent = chunk;
          inner.style.setProperty('--fd-wd', Math.min(state.i * 55, 700) + 'ms');
          state.i++;
          w.appendChild(inner);
          frag.appendChild(w);
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1 && !child.classList.contains('fd-word')) {
        splitNode(child, state);
      }
    });
  }

  function initSplit() {
    all('[data-fd-split]').forEach(function (el) {
      if (!once(el, 'split')) return;
      if (REDUCED) {
        el.classList.add('fd-revealed');
        return;
      }
      splitNode(el, { i: 0 });
      el.setAttribute('data-fd-reveal', 'fade');
    });
  }

  /* ==================================================================
     3. COUNTDOWN
     Australian Father's Day = first Sunday of September.
     ================================================================== */
  function auFathersDay() {
    var now = new Date();
    function firstSunday(year) {
      var d = new Date(year, 8, 1, 0, 0, 0, 0);
      return new Date(year, 8, 1 + ((7 - d.getDay()) % 7), 23, 59, 59, 0);
    }
    var target = firstSunday(now.getFullYear());
    if (target.getTime() <= now.getTime()) target = firstSunday(now.getFullYear() + 1);
    return target;
  }

  var UNITS = [
    ['days', 'Days'],
    ['hours', 'Hrs'],
    ['minutes', 'Mins'],
    ['seconds', 'Secs']
  ];

  function buildCountdown(el) {
    var showSecs = el.dataset.fdSeconds !== 'false';
    el.innerHTML = '';
    UNITS.forEach(function (u) {
      if (u[0] === 'seconds' && !showSecs) return;
      var box = document.createElement('div');
      box.className = 'fd-cd__unit';
      box.innerHTML =
        '<span class="fd-cd__num" data-u="' + u[0] + '">--</span><span class="fd-cd__lbl">' + u[1] + '</span>';
      el.appendChild(box);
    });
  }

  function tickCountdown(el) {
    var raw = el.dataset.fdTarget;
    var target;
    if (raw && raw !== 'auto') {
      target = new Date(raw.replace(' ', 'T'));
      if (isNaN(target.getTime())) target = auFathersDay();
    } else {
      target = auFathersDay();
    }

    var diff = Math.max(0, target.getTime() - Date.now());
    var s = Math.floor(diff / 1000);
    var vals = {
      days: Math.floor(s / 86400),
      hours: Math.floor((s % 86400) / 3600),
      minutes: Math.floor((s % 3600) / 60),
      seconds: s % 60
    };

    all('[data-u]', el).forEach(function (n) {
      var v = vals[n.dataset.u];
      var str = v < 10 ? '0' + v : String(v);
      if (n.textContent !== str) {
        n.textContent = str;
        if (!REDUCED) {
          n.classList.remove('fd-tick');
          void n.offsetWidth;
          n.classList.add('fd-tick');
        }
      }
    });

    if (diff === 0) {
      var done = el.dataset.fdExpired;
      if (done) el.innerHTML = '<span class="fd-cd__lbl" style="font-size:.8rem">' + done + '</span>';
      return false;
    }
    return true;
  }

  var cdTimer = null;
  function initCountdowns() {
    var els = all('[data-fd-countdown]');
    els.forEach(function (el) {
      if (!once(el, 'countdown')) return;
      buildCountdown(el);
      tickCountdown(el);
    });
    if (els.length && !cdTimer) {
      cdTimer = setInterval(function () {
        all('[data-fd-countdown]').forEach(tickCountdown);
      }, 1000);
    }
  }

  /* ==================================================================
     4. NUMBER COUNT-UP
     ================================================================== */
  function initCounters() {
    var nodes = all('[data-fd-count]').filter(function (el) {
      return once(el, 'counter');
    });
    if (!nodes.length) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.unobserve(e.target);
          run(e.target);
        });
      },
      { threshold: 0.4 }
    );

    function run(el) {
      var end = parseFloat(el.dataset.fdCount) || 0;
      var dec = parseInt(el.dataset.fdDecimals, 10) || 0;
      var pre = el.dataset.fdPrefix || '';
      var suf = el.dataset.fdSuffix || '';
      if (REDUCED) {
        el.textContent = pre + end.toLocaleString(undefined, { minimumFractionDigits: dec }) + suf;
        return;
      }
      var dur = parseInt(el.dataset.fdDuration, 10) || 1600;
      var t0 = performance.now();
      (function frame(t) {
        var p = Math.min(1, (t - t0) / dur);
        var eased = 1 - Math.pow(1 - p, 3);
        var v = end * eased;
        el.textContent =
          pre +
          v.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec }) +
          suf;
        if (p < 1) requestAnimationFrame(frame);
      })(t0);
    }

    nodes.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ==================================================================
     5. MARQUEE — duplicate the track so the loop is seamless
     ================================================================== */
  function initMarquees() {
    all('[data-fd-marquee]').forEach(function (mq) {
      if (!once(mq, 'marquee')) return;
      var track = mq.querySelector('.fd-marquee__track');
      if (!track) return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      mq.appendChild(clone);
    });
  }

  /* ==================================================================
     6. 3D TILT
     ================================================================== */
  function initTilt() {
    if (!HOVER || REDUCED) return;
    all('[data-fd-tilt]').forEach(function (el) {
      if (!once(el, 'tilt')) return;
      el.classList.add('fd-tilt');
      var max = parseFloat(el.dataset.fdTilt) || 7;

      el.addEventListener('pointermove', function (ev) {
        var r = el.getBoundingClientRect();
        var px = (ev.clientX - r.left) / r.width - 0.5;
        var py = (ev.clientY - r.top) / r.height - 0.5;
        el.classList.add('fd-tilting');
        el.style.setProperty('--fd-ry', (px * max).toFixed(2) + 'deg');
        el.style.setProperty('--fd-rx', (-py * max).toFixed(2) + 'deg');
      });

      el.addEventListener('pointerleave', function () {
        el.classList.remove('fd-tilting');
        el.style.setProperty('--fd-ry', '0deg');
        el.style.setProperty('--fd-rx', '0deg');
      });
    });
  }

  /* ==================================================================
     7. PARALLAX (transform-only, rAF-throttled)
     ================================================================== */
  var parallaxNodes = [];
  var parallaxQueued = false;

  function paintParallax() {
    parallaxQueued = false;
    var vh = window.innerHeight;
    parallaxNodes.forEach(function (item) {
      var r = item.el.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) return;
      var progress = (r.top + r.height / 2 - vh / 2) / vh;
      item.el.style.transform = 'translate3d(0,' + (-progress * item.amt).toFixed(1) + 'px,0)';
    });
  }

  function initParallax() {
    if (REDUCED) return;
    var fresh = all('[data-fd-parallax]').filter(function (el) {
      return once(el, 'parallax');
    });
    fresh.forEach(function (el) {
      parallaxNodes.push({ el: el, amt: parseFloat(el.dataset.fdParallax) || 40 });
    });
    if (parallaxNodes.length && !window.__fdParaBound) {
      window.__fdParaBound = true;
      window.addEventListener(
        'scroll',
        function () {
          if (parallaxQueued) return;
          parallaxQueued = true;
          requestAnimationFrame(paintParallax);
        },
        { passive: true }
      );
    }
    paintParallax();
  }

  /* ==================================================================
     8. SCROLL PROGRESS RAIL
     ================================================================== */
  function initProgress() {
    var bar = document.querySelector('[data-fd-progress]');
    if (!bar || !once(bar, 'progress')) return;
    function paint() {
      var h = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.setProperty('--fd-p', h > 0 ? Math.min(1, window.scrollY / h).toFixed(4) : 0);
    }
    window.addEventListener('scroll', paint, { passive: true });
    window.addEventListener('resize', paint, { passive: true });
    paint();
  }

  /* ==================================================================
     9. ACCORDION
     ================================================================== */
  function initAccordions() {
    all('[data-fd-acc]').forEach(function (group) {
      if (!once(group, 'accordion')) return;
      var single = group.dataset.fdAcc === 'single';

      group.addEventListener('click', function (ev) {
        var btn = ev.target.closest('.fd-acc__btn');
        if (!btn || !group.contains(btn)) return;
        var item = btn.closest('.fd-acc');
        var open = item.classList.contains('is-open');

        if (single && !open) {
          all('.fd-acc.is-open', group).forEach(function (o) {
            o.classList.remove('is-open');
            var b = o.querySelector('.fd-acc__btn');
            if (b) b.setAttribute('aria-expanded', 'false');
          });
        }
        item.classList.toggle('is-open', !open);
        btn.setAttribute('aria-expanded', String(!open));
      });
    });
  }

  /* ==================================================================
     10. INSTANT FILTER PILLS + SORT
     ================================================================== */
  function initFilters() {
    all('[data-fd-filter-root]').forEach(function (root) {
      if (!once(root, 'filter')) return;

      var grid = root.querySelector('[data-fd-grid]');
      var slots = all('[data-fd-slot]', grid || root);
      var pills = all('[data-fd-filter]', root);
      var counter = root.querySelector('[data-fd-count-shown]');
      var empty = root.querySelector('[data-fd-empty]');
      var sortSel = root.querySelector('[data-fd-sort]');
      if (!grid || !slots.length) return;

      // Cache the server-rendered order so "Featured" can be restored.
      slots.forEach(function (s, i) {
        s.dataset.fdOrder = String(i);
      });

      var activeKey = 'all';

      function hasKey(slot, key) {
        if (key === 'all') return true;
        return (slot.dataset.fdKeys || '').split('|').indexOf(key) > -1;
      }
      function matches(slot) {
        return hasKey(slot, activeKey);
      }

      // Label each pill with how many products it would show, and hide
      // pills that would land the shopper on an empty grid.
      pills.forEach(function (pill) {
        var key = pill.dataset.fdFilter || 'all';
        var n = slots.filter(function (slot) {
          return hasKey(slot, key);
        }).length;
        var out = pill.querySelector('[data-fd-pill-count]');
        if (out) out.textContent = n ? String(n) : '';
        if (!n && key !== 'all') pill.hidden = true;
      });
      pills = pills.filter(function (p) {
        return !p.hidden;
      });

      function apply(animate) {
        var shown = 0;
        slots.forEach(function (slot) {
          var ok = matches(slot);
          if (ok) shown++;
          if (!animate || REDUCED) {
            slot.hidden = !ok;
            slot.classList.remove('fd-filtering-out');
            return;
          }
          if (ok) {
            slot.hidden = false;
            requestAnimationFrame(function () {
              slot.classList.remove('fd-filtering-out');
            });
          } else {
            slot.classList.add('fd-filtering-out');
            setTimeout(function () {
              if (!matches(slot)) slot.hidden = true;
            }, 320);
          }
        });
        if (counter) counter.textContent = String(shown);
        if (empty) empty.hidden = shown !== 0;
      }

      pills.forEach(function (pill) {
        pill.addEventListener('click', function () {
          activeKey = pill.dataset.fdFilter || 'all';
          pills.forEach(function (p) {
            p.setAttribute('aria-pressed', String(p === pill));
          });
          apply(true);
        });
      });

      if (sortSel) {
        sortSel.addEventListener('change', function () {
          var mode = sortSel.value;
          var ordered = slots.slice().sort(function (a, b) {
            if (mode === 'price-asc') return num(a, 'fdPrice') - num(b, 'fdPrice');
            if (mode === 'price-desc') return num(b, 'fdPrice') - num(a, 'fdPrice');
            if (mode === 'title-asc') return (a.dataset.fdTitle || '').localeCompare(b.dataset.fdTitle || '');
            if (mode === 'best') return num(b, 'fdRating') - num(a, 'fdRating');
            return num(a, 'fdOrder') - num(b, 'fdOrder');
          });
          ordered.forEach(function (s) {
            grid.appendChild(s);
          });
        });
      }

      function num(el, key) {
        return parseFloat(el.dataset[key]) || 0;
      }

      apply(false);
    });
  }

  /* ==================================================================
     11. QUICK ADD → Shopify AJAX cart
     ================================================================== */
  function toast(msg) {
    var t = document.querySelector('.fd-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'fd-toast fd-scope';
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      document.body.appendChild(t);
    }
    t.innerHTML =
      '<svg class="fd-toast__ico" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg><span></span>';
    t.querySelector('span').textContent = msg;
    requestAnimationFrame(function () {
      t.classList.add('is-on');
    });
    clearTimeout(t.__timer);
    t.__timer = setTimeout(function () {
      t.classList.remove('is-on');
    }, 3200);
  }

  function confetti(x, y) {
    if (REDUCED) return;
    var colors = ['#f2b441', '#e9642b', '#0e6b5a', '#ffb27a', '#b3234b'];
    var box = document.createElement('div');
    box.className = 'fd-confetti';
    for (var i = 0; i < 26; i++) {
      var p = document.createElement('i');
      var ang = Math.random() * Math.PI * 2;
      var dist = 70 + Math.random() * 150;
      p.style.setProperty('--x', x + 'px');
      p.style.setProperty('--y', y + 'px');
      p.style.setProperty('--tx', (Math.cos(ang) * dist).toFixed(0) + 'px');
      p.style.setProperty('--ty', (Math.sin(ang) * dist - 60).toFixed(0) + 'px');
      p.style.setProperty('--c', colors[i % colors.length]);
      p.style.setProperty('--r', (Math.random() * 900 - 450).toFixed(0) + 'deg');
      p.style.setProperty('--d', (0.8 + Math.random() * 0.6).toFixed(2) + 's');
      p.style.setProperty('--dl', (Math.random() * 0.12).toFixed(2) + 's');
      p.style.setProperty('--w', (5 + Math.random() * 5).toFixed(0) + 'px');
      p.style.setProperty('--h', (9 + Math.random() * 8).toFixed(0) + 'px');
      box.appendChild(p);
    }
    document.body.appendChild(box);
    setTimeout(function () {
      box.remove();
    }, 1800);
  }

  function refreshCartUI() {
    // Nudge whatever cart the host theme uses to re-read its state.
    document.dispatchEvent(new CustomEvent('fd:cart:updated', { bubbles: true }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (typeof window.Shopify !== 'undefined' && window.Shopify.onCartUpdate) {
      try {
        window.Shopify.onCartUpdate();
      } catch (e) {
        /* host theme hook is optional */
      }
    }
  }

  function initQuickAdd() {
    all('[data-fd-quick-add]').forEach(function (form) {
      if (!once(form, 'quickAdd')) return;

      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        var btn = form.querySelector('button[type="submit"]');
        var label = btn ? btn.innerHTML : '';
        if (btn) {
          btn.disabled = true;
          btn.innerHTML = 'Adding…';
        }

        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [
              {
                id: Number(form.querySelector('[name="id"]').value),
                quantity: Number((form.querySelector('[name="quantity"]') || {}).value || 1)
              }
            ]
          })
        })
          .then(function (r) {
            return r.json().then(function (data) {
              if (!r.ok) throw new Error(data.description || data.message || 'Could not add to cart');
              return data;
            });
          })
          .then(function () {
            toast(form.dataset.fdAddedText || 'Added to cart — nice pick for Dad!');
            if (btn) {
              var r = btn.getBoundingClientRect();
              confetti(r.left + r.width / 2, r.top + r.height / 2);
            }
            refreshCartUI();
          })
          .catch(function (err) {
            toast(err.message || 'Sorry — that one could not be added.');
          })
          .finally(function () {
            if (btn) {
              btn.disabled = false;
              btn.innerHTML = label;
            }
          });
      });
    });
  }

  /* ==================================================================
     12. GIFT FINDER
     ================================================================== */
  function initGiftFinder() {
    all('[data-fd-finder]').forEach(function (root) {
      if (!once(root, 'finder')) return;

      var steps = all('[data-fd-step]', root);
      var result = root.querySelector('[data-fd-result]');
      var bar = root.querySelector('[data-fd-finder-bar]');
      var restart = root.querySelector('[data-fd-restart]');
      var answers = {};
      var idx = 0;

      function show(i) {
        idx = i;
        steps.forEach(function (s, n) {
          s.hidden = n !== i;
          if (n === i) s.classList.add('fd-revealed');
        });
        if (result) result.hidden = i < steps.length;
        if (bar) bar.style.setProperty('--fd-p', ((i / steps.length) * 100).toFixed(0) + '%');
      }

      function finish() {
        if (bar) bar.style.setProperty('--fd-p', '100%');
        steps.forEach(function (s) {
          s.hidden = true;
        });
        if (!result) return;
        result.hidden = false;

        // Pick the outcome whose data-fd-match tags all appear in the answers.
        var picked = null;
        var chosen = Object.keys(answers).map(function (k) {
          return answers[k];
        });
        all('[data-fd-outcome]', result).forEach(function (out) {
          out.hidden = true;
          var need = (out.dataset.fdOutcome || '').split('|').filter(Boolean);
          if (!picked && need.length && need.every(function (n) { return chosen.indexOf(n) > -1; })) picked = out;
        });
        if (!picked) picked = result.querySelector('[data-fd-outcome-default]') || result.querySelector('[data-fd-outcome]');
        if (picked) {
          picked.hidden = false;
          picked.classList.add('fd-revealed');
        }

        var r = result.getBoundingClientRect();
        confetti(r.left + r.width / 2, Math.max(120, r.top + 60));
      }

      root.addEventListener('click', function (ev) {
        var opt = ev.target.closest('[data-fd-answer]');
        if (opt && root.contains(opt)) {
          answers['q' + idx] = opt.dataset.fdAnswer;
          if (idx + 1 < steps.length) show(idx + 1);
          else finish();
          return;
        }
        var back = ev.target.closest('[data-fd-back]');
        if (back && idx > 0) show(idx - 1);
      });

      if (restart) {
        restart.addEventListener('click', function () {
          answers = {};
          if (result) result.hidden = true;
          show(0);
        });
      }

      show(0);
    });
  }

  /* ==================================================================
     13. STICKY BAR — appears once the hero is scrolled past
     ================================================================== */
  function initStickyBar() {
    all('[data-fd-sticky]').forEach(function (bar) {
      if (!once(bar, 'sticky')) return;
      var after = parseInt(bar.dataset.fdSticky, 10) || 600;
      function paint() {
        bar.classList.toggle('is-on', window.scrollY > after);
      }
      window.addEventListener('scroll', paint, { passive: true });
      paint();
    });
  }

  /* ==================================================================
     14. SMOOTH IN-PAGE ANCHORS
     ================================================================== */
  function initAnchors() {
    if (once(document.documentElement, 'anchors')) {
      document.addEventListener('click', function (ev) {
        var a = ev.target.closest('a[href^="#"]');
        if (!a) return;
        var id = a.getAttribute('href');
        if (!id || id === '#') return;
        var t = document.querySelector(id);
        if (!t || !t.closest('.fd-scope')) return;
        ev.preventDefault();
        t.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
      });
    }
  }

  /* ==================================================================
     BOOT
     ================================================================== */
  function init() {
    initSplit();
    initReveal();
    initCountdowns();
    initCounters();
    initMarquees();
    initTilt();
    initParallax();
    initProgress();
    initAccordions();
    initFilters();
    initQuickAdd();
    initGiftFinder();
    initStickyBar();
    initAnchors();
  }

  window.FDCore = { __v: 1, init: init, toast: toast, confetti: confetti };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme Editor: re-scan when a section is added / re-rendered.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', init);
})();
